import {
  Stack, Title, SimpleGrid, Paper, Text, Group,
  RingProgress, SegmentedControl, Select, Tabs, Badge, ThemeIcon, Button,
} from '@mantine/core'
import { AreaChart, BarChart } from '@mantine/charts'
import {
  IconClipboardList, IconShieldCheck, IconClock, IconHourglass, IconChartBar,
  IconInbox, IconAlertTriangle, IconArrowRight, IconUsersGroup,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { organizationsApi } from '@/api/organizations'
import { useCurrentTenant, useAuthStore, useCan } from '@/store/auth'
import { useEffect, useMemo, useRef } from 'react'
// Relatorio gerencial dizendo '0 chamados abertos, 0 atrasados' quando a metrica nao veio.
import { metrica } from '@/utils/metrica'

interface OrgUser { user_id: number; full_name: string; email: string; role: string }

interface VolumeData { period: string; data: { period: string; total: number }[] }
interface CategoryData { data: { category: string; total: number }[] }
interface SlaData { total_with_sla: number; within_sla: number; compliance_pct: number | null }
interface ResponseTimeData { avg_first_response_hours: number | null }
interface ResolutionTimeData { avg_resolution_hours: number | null }
interface BacklogData { open_count: number; overdue_count: number }
interface TeamLoadRow {
  user_id: number; full_name: string; open_count: number
  in_progress_count: number; waiting_count: number; avg_resolution_hours: number | null
}

interface GmudMetrics {
  total: number
  by_classification: { standard: number; normal: number; urgent: number; emergency: number }
  execution: { completed: number; failed: number; rolled_back: number; success_rate_pct: number | null }
}

function MetricCard({
  title, value, subtitle, color = 'inovareTeal', icon,
}: {
  title: string; value: string; subtitle?: string; color?: string; icon?: React.ReactNode
}) {
  return (
    <Paper p="md" withBorder style={{ borderLeft: `3px solid var(--mantine-color-${color}-6)` }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.03em' }}>
            {title}
          </Text>
          <Text fw={700} mt={4} lh={1.1} style={{ fontSize: '1.6rem' }}>{value}</Text>
          {subtitle && <Text size="xs" c="dimmed" mt={2}>{subtitle}</Text>}
        </div>
        {icon && (
          <ThemeIcon variant="light" color={color} size={40} radius="md">{icon}</ThemeIcon>
        )}
      </Group>
    </Paper>
  )
}

const firstName = (full: string) => full.split(' ')[0] || full

function fmtPeriod(iso: string, gran: string): string {
  const d = new Date(iso.replace(' ', 'T'))
  if (isNaN(d.getTime())) return iso
  if (gran === 'month') return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

export function Relatorios() {
  const currentTenant = useCurrentTenant()
  const tenantId = currentTenant?.id
  const { user, selectedOrgId, globalScope } = useAuthStore()
  const can = useCan()
  // Os GETs de gmud/metrics agora exigem gmud_read no backend — sem a
  // permissao, esconder a aba E desligar a query (senao fica 403 no
  // console por uma requisicao que sabemos que sera recusada).
  const canGmudRead = can('chamados', 'gmud_read')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Filtros com estado na URL (persistência + compartilhável).
  const period = searchParams.get('period') || 'month'
  const collaborator = searchParams.get('colab') || null // null = Geral
  const tenant = searchParams.get('tenant') || null       // null = toda a organização

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }
  const setPeriod = (v: string) => setParam('period', v === 'month' ? null : v)
  const setCollaborator = (v: string | null) => setParam('colab', v)
  const setTenant = (v: string | null) => setParam('tenant', v)

  // Ao trocar de organização/escopo, limpa colaborador + tenant (mas honra a URL no mount).
  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    const next = new URLSearchParams(searchParams)
    next.delete('colab'); next.delete('tenant')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, globalScope])

  // Escopo das métricas de chamados: global > org específica > tenant (fallback).
  const metricBase = globalScope
    ? '/v1/chamados/metrics'
    : selectedOrgId
    ? `/v1/organizations/${selectedOrgId}/chamados/metrics`
    : `/v1/tenants/${tenantId}/workflow/metrics`
  const scopeReady = globalScope || !!selectedOrgId || !!tenantId

  // Anexa assignee_id + tenant_id (quando aplicáveis) à URL da métrica.
  const withParams = (url: string) => {
    const parts: string[] = []
    if (collaborator) parts.push(`assignee_id=${collaborator}`)
    if (tenant) parts.push(`tenant_id=${tenant}`)
    if (!parts.length) return url
    return `${url}${url.includes('?') ? '&' : '?'}${parts.join('&')}`
  }

  // Colaboradores da org ativa (só com uma org específica selecionada).
  const { data: orgUsersResp } = useQuery<{ items: OrgUser[] }>({
    queryKey: ['org-users', selectedOrgId],
    queryFn: async () => (await api.get<{ items: OrgUser[] }>(`/v1/organizations/${selectedOrgId}/users`)).data,
    enabled: !!selectedOrgId,
  })
  const orgUsers = orgUsersResp?.items ?? []

  // Tenants da org ativa (interseção membros da org ∩ tenants acessíveis do usuário).
  const { data: orgDetail } = useQuery({
    queryKey: ['organization', selectedOrgId],
    queryFn: () => organizationsApi.get(selectedOrgId as number),
    enabled: !!selectedOrgId,
  })
  const userTenantIds = useMemo(() => new Set((user?.tenants ?? []).map((t) => t.id)), [user])
  const orgTenants = useMemo(
    () => (orgDetail?.members ?? []).filter((m) => userTenantIds.has(m.tenant_id)),
    [orgDetail, userTenantIds],
  )

  const { data: volumeData } = useQuery<VolumeData>({
    queryKey: ['metrics-volume', metricBase, period, collaborator, tenant],
    queryFn: async () => (await api.get<VolumeData>(withParams(`${metricBase}/volume?period=${period}`))).data,
    enabled: scopeReady,
  })

  const { data: categoryData } = useQuery<CategoryData>({
    queryKey: ['metrics-category', metricBase, collaborator, tenant],
    queryFn: async () => (await api.get<CategoryData>(withParams(`${metricBase}/by-category`))).data,
    enabled: scopeReady,
  })

  const { data: slaData } = useQuery<SlaData>({
    queryKey: ['metrics-sla', metricBase, collaborator, tenant],
    queryFn: async () => (await api.get<SlaData>(withParams(`${metricBase}/sla-compliance`))).data,
    enabled: scopeReady,
  })

  const { data: responseTime } = useQuery<ResponseTimeData>({
    queryKey: ['metrics-response-time', metricBase, collaborator, tenant],
    queryFn: async () => (await api.get<ResponseTimeData>(withParams(`${metricBase}/response-time`))).data,
    enabled: scopeReady,
  })

  const { data: resolutionTime } = useQuery<ResolutionTimeData>({
    queryKey: ['metrics-resolution-time', metricBase, collaborator, tenant],
    queryFn: async () => (await api.get<ResolutionTimeData>(withParams(`${metricBase}/resolution-time`))).data,
    enabled: scopeReady,
  })

  const { data: backlog } = useQuery<BacklogData>({
    queryKey: ['metrics-backlog', metricBase, collaborator, tenant],
    queryFn: async () => (await api.get<BacklogData>(withParams(`${metricBase}/backlog`))).data,
    enabled: scopeReady,
  })

  // Carga por colaborador: só faz sentido quando NÃO filtrado a um colaborador.
  const { data: teamLoadResp } = useQuery<{ data: TeamLoadRow[] }>({
    queryKey: ['metrics-team-load', metricBase, tenant],
    queryFn: async () => (await api.get<{ data: TeamLoadRow[] }>(withParams(`${metricBase}/team-load`))).data,
    enabled: scopeReady && !collaborator,
  })

  const { data: gmudMetrics } = useQuery<GmudMetrics>({
    queryKey: ['gmud-metrics', tenantId],
    queryFn: async () => (await api.get<GmudMetrics>(`/v1/tenants/${tenantId}/gmud/metrics`)).data,
    enabled: !!tenantId && canGmudRead,
  })

  const totalVolume = volumeData?.data.reduce((s, d) => s + d.total, 0) ?? 0
  const slaCompliance = slaData?.compliance_pct
  const gmudSuccessRate = gmudMetrics?.execution.success_rate_pct

  const volumeSeries = useMemo(
    () => (volumeData?.data ?? []).map((d) => ({ periodo: fmtPeriod(d.period, period), Chamados: d.total })),
    [volumeData, period],
  )
  const teamRows = useMemo(
    () => (teamLoadResp?.data ?? []).filter((r) => r.open_count > 0).slice(0, 12)
      .map((r) => ({ nome: firstName(r.full_name), Abertos: r.open_count })),
    [teamLoadResp],
  )

  // Rótulos do resumo de escopo.
  const colabLabel = !collaborator
    ? 'toda a equipe'
    : collaborator === String(user?.id)
    ? 'Meus chamados'
    : orgUsers.find((u) => String(u.user_id) === collaborator)?.full_name ?? 'colaborador'
  const tenantLabel = tenant
    ? orgTenants.find((m) => String(m.tenant_id) === tenant)?.tenant_name ?? 'tenant'
    : selectedOrgId
    ? 'toda a organização'
    : globalScope
    ? 'todas as organizações'
    : currentTenant?.name ?? 'tenant atual'
  const periodLabel = period === 'day' ? 'diário' : period === 'week' ? 'semanal' : 'mensal'

  const goToList = () => {
    const p = new URLSearchParams()
    if (selectedOrgId) p.set('organization_id', String(selectedOrgId))
    if (tenant) p.set('tenant_ids', tenant)
    if (collaborator) p.set('assignee_id', collaborator)
    navigate(`/chamados${p.toString() ? `?${p.toString()}` : ''}`)
  }

  return (
    <Stack gap="md">
      <Group gap="sm" align="center">
        <span
          style={{
            display: 'grid', placeItems: 'center', width: 40, height: 40,
            borderRadius: 'var(--mantine-radius-md)', color: '#fff',
            background: 'var(--ai-brand-gradient)', boxShadow: 'var(--ai-shadow-glow)',
            flexShrink: 0,
          }}
        >
          <IconChartBar size={22} />
        </span>
        <div>
          <Title order={2} lh={1.1}>Dashboard</Title>
          <Text size="sm" c="dimmed">Indicadores de chamados e mudanças</Text>
        </div>
      </Group>
      <Tabs defaultValue="chamados">
        <Tabs.List mb="md">
          <Tabs.Tab value="chamados">Chamados</Tabs.Tab>
          {canGmudRead && <Tabs.Tab value="gmud">GMUD</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="chamados">
          <Stack gap="md">
      {/* Barra de filtros unificada */}
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Text fw={500}>Métricas de Chamados</Text>
        <Group gap="sm" wrap="wrap" align="flex-end">
          <Select
            size="xs"
            label="Colaborador"
            placeholder={selectedOrgId ? 'Geral' : 'Selecione uma organização'}
            disabled={!selectedOrgId}
            value={collaborator}
            onChange={setCollaborator}
            clearable
            searchable
            w={220}
            data={[
              { value: '', label: 'Geral' },
              ...(user?.id ? [{ value: String(user.id), label: 'Meus chamados' }] : []),
              ...orgUsers
                .filter((u) => u.user_id !== user?.id)
                .map((u) => ({
                  value: String(u.user_id),
                  label: `${u.full_name}${u.role === 'gestor' ? ' · gestor' : ''}`,
                })),
            ]}
          />
          {selectedOrgId && (
            <Select
              size="xs"
              label="Tenant / Ambiente"
              placeholder="Toda a organização"
              value={tenant}
              onChange={setTenant}
              clearable
              searchable
              w={220}
              data={[
                { value: '', label: 'Toda a organização' },
                ...orgTenants.map((m) => ({
                  value: String(m.tenant_id),
                  label: `${m.tenant_name}${m.environment ? ` · ${m.environment.toUpperCase()}` : ''}`,
                })),
              ]}
            />
          )}
          <SegmentedControl
            size="xs"
            value={period}
            onChange={setPeriod}
            data={[
              { value: 'day', label: 'Diário' },
              { value: 'week', label: 'Semanal' },
              { value: 'month', label: 'Mensal' },
            ]}
          />
        </Group>
      </Group>

      {/* Resumo do escopo aplicado + drill-down */}
      <Paper p="sm" withBorder radius="md" style={{ background: 'var(--ai-card-bg)' }}>
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text size="sm" c="dimmed">
            Mostrando chamados de <Text span fw={600} c="inherit">{colabLabel}</Text>
            {' · '}<Text span fw={600} c="inherit">{tenantLabel}</Text>
            {' · período '}<Text span fw={600} c="inherit">{periodLabel}</Text>
          </Text>
          <Button
            size="xs"
            variant="light"
            rightSection={<IconArrowRight size={14} />}
            onClick={goToList}
          >
            Ver chamados
          </Button>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
        <MetricCard
          title="Total de Chamados"
          value={String(totalVolume)}
          subtitle="Últimos 90 dias"
          color="inovareTeal"
          icon={<IconClipboardList size={22} />}
        />
        <MetricCard
          title="Backlog Aberto"
          value={metrica(backlog?.open_count, String)}
          subtitle="Não resolvidos"
          color="blue"
          icon={<IconInbox size={22} />}
        />
        <MetricCard
          title="SLA Vencidos"
          value={metrica(backlog?.overdue_count, String)}
          subtitle="Aberto e em atraso"
          color={backlog?.overdue_count ? 'red' : 'gray'}
          icon={<IconAlertTriangle size={22} />}
        />
        <MetricCard
          title="SLA Compliance"
          value={slaCompliance != null ? `${slaCompliance}%` : '—'}
          subtitle={`${slaData?.within_sla ?? 0}/${slaData?.total_with_sla ?? 0} no prazo`}
          color={slaCompliance != null && slaCompliance < 90 ? 'red' : 'green'}
          icon={<IconShieldCheck size={22} />}
        />
        <MetricCard
          title="Tempo Médio 1ª Resposta"
          value={responseTime?.avg_first_response_hours != null
            ? `${responseTime.avg_first_response_hours.toFixed(1)}h`
            : '—'}
          subtitle="Últimos 30 dias"
          color="blue"
          icon={<IconClock size={22} />}
        />
        <MetricCard
          title="Tempo Médio Resolução"
          value={resolutionTime?.avg_resolution_hours != null
            ? `${resolutionTime.avg_resolution_hours.toFixed(1)}h`
            : '—'}
          subtitle="Últimos 30 dias"
          color="orange"
          icon={<IconHourglass size={22} />}
        />
      </SimpleGrid>

      {/* Tendência de volume */}
      <Paper shadow="xs" p="md" withBorder>
        <Text fw={500} mb="md">Tendência de Volume (últimos 90 dias)</Text>
        {volumeSeries.length > 0 ? (
          <AreaChart
            h={220}
            data={volumeSeries}
            dataKey="periodo"
            series={[{ name: 'Chamados', color: 'inovareTeal.6' }]}
            curveType="monotone"
            withDots={volumeSeries.length <= 31}
            tickLine="x"
            gridAxis="xy"
            withGradient
          />
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="xl">Sem chamados no período selecionado.</Text>
        )}
      </Paper>

      {slaCompliance != null && (
        <Paper shadow="xs" p="md" withBorder>
          <Group>
            <RingProgress
              size={100}
              thickness={12}
              sections={[
                { value: slaCompliance, color: slaCompliance >= 80 ? 'teal' : slaCompliance >= 60 ? 'orange' : 'red' },
              ]}
              label={<Text size="xs" ta="center">{slaCompliance}%</Text>}
            />
            <Stack gap={2}>
              <Text fw={500}>SLA Compliance</Text>
              <Text size="sm" c="dimmed">
                {slaData?.within_sla} de {slaData?.total_with_sla} chamados resolvidos dentro do prazo (últimos 30 dias)
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Carga por colaborador (oculto quando filtrado a um colaborador) */}
      {!collaborator && teamRows.length > 0 && (
        <Paper shadow="xs" p="md" withBorder>
          <Group gap="xs" mb="md">
            <IconUsersGroup size={18} />
            <Text fw={500}>Carga por Colaborador (chamados abertos)</Text>
          </Group>
          <BarChart
            h={Math.max(160, teamRows.length * 40)}
            data={teamRows}
            dataKey="nome"
            orientation="vertical"
            series={[{ name: 'Abertos', color: 'inovareTeal.6' }]}
            withTooltip
            gridAxis="none"
          />
        </Paper>
      )}

      {categoryData && categoryData.data.length > 0 && (
        <Paper shadow="xs" p="md" withBorder>
          <Text fw={500} mb="md">Volume por Categoria (últimos 30 dias)</Text>
          <Stack gap="xs">
            {categoryData.data.map((d) => (
              <Group key={d.category} justify="space-between">
                <Text size="sm">{d.category}</Text>
                <Text size="sm" fw={600}>{d.total}</Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
          </Stack>
        </Tabs.Panel>

        {canGmudRead && (
        <Tabs.Panel value="gmud">
          <Stack gap="md">
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              <MetricCard
                title="Total GMUDs"
                value={metrica(gmudMetrics?.total, String)}
              />
              <MetricCard
                title="Taxa de Sucesso"
                value={gmudSuccessRate != null ? `${gmudSuccessRate.toFixed(1)}%` : '—'}
              />
              <MetricCard
                title="Completadas"
                value={metrica(gmudMetrics?.execution.completed, String)}
              />
              <MetricCard
                title="Falhas"
                value={metrica(gmudMetrics?.execution.failed, String)}
                subtitle={gmudMetrics?.execution.rolled_back ? `${gmudMetrics.execution.rolled_back} rollback(s)` : undefined}
              />
            </SimpleGrid>

            {gmudMetrics && (
              <Paper shadow="xs" p="md" withBorder>
                <Text fw={500} mb="md">Por Classificação</Text>
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                  {(['standard', 'normal', 'urgent', 'emergency'] as const).map((cls) => (
                    <Group key={cls} justify="space-between">
                      <Badge
                        variant="light"
                        color={cls === 'emergency' ? 'red' : cls === 'urgent' ? 'orange' : cls === 'normal' ? 'blue' : 'gray'}
                      >
                        {cls}
                      </Badge>
                      <Text fw={600}>{gmudMetrics.by_classification[cls]}</Text>
                    </Group>
                  ))}
                </SimpleGrid>
              </Paper>
            )}

            {gmudSuccessRate != null && (
              <Paper shadow="xs" p="md" withBorder>
                <Group>
                  <RingProgress
                    size={100}
                    thickness={12}
                    sections={[
                      { value: gmudSuccessRate, color: gmudSuccessRate >= 80 ? 'teal' : gmudSuccessRate >= 60 ? 'orange' : 'red' },
                    ]}
                    label={<Text size="xs" ta="center">{gmudSuccessRate.toFixed(1)}%</Text>}
                  />
                  <Stack gap={2}>
                    <Text fw={500}>Taxa de Sucesso</Text>
                    <Text size="sm" c="dimmed">
                      {gmudMetrics?.execution.completed} completadas de {gmudMetrics?.total} GMUDs
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  )
}
